import { useState } from 'react';
import { trackProjectImportStarted, trackProjectImportSuccess, trackProjectImportFailed } from '../utils/analytics';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { mergePdfLines } from '../utils/pdfText';
import type { PdfLine, PdfTextItem } from '../utils/pdfText';
import { convertOdtToHtml } from '../utils/odtToHtml';
import { convertEpubToHtml } from '../utils/epubToHtml';
import { API_BASE } from '../config';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { convertHtmlToYjs } from '../utils/html-to-yjs';
import { MAX_IMPORT_PDF_FILE_BYTES } from '../utils/yjs-content-payload';
import { extractFirstChapter, htmlToPlainText } from '../utils/first-chapter';
import { takeFirstWords } from '../utils/word-count';
import { uploadImageResourceToS3 } from '../utils/imageResources';

const SUGGESTIONS_MAX_WORDS = 1500;

export interface ImportFileResult {
  success: boolean;
  documentId: string | null;
  projectId: string | null;
  firstChapterWordCount: number | null;
  suggestionsText: string;
  manuscriptText: string;
}

const FAILED_IMPORT_RESULT: ImportFileResult = {
  success: false,
  documentId: null,
  projectId: null,
  firstChapterWordCount: null,
  suggestionsText: '',
  manuscriptText: '',
};

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface ProcessedFile {
  html: string;
  importType: 'word' | 'pdf' | 'epub';
  fileName: string;
}

async function processFile(file: File): Promise<ProcessedFile> {
  const fileName = file.name.toLowerCase();
  const isPdf = fileName.endsWith('.pdf');
  const isOdt = fileName.endsWith('.odt');
  const isEpub = fileName.endsWith('.epub');
  const arrayBuffer = await file.arrayBuffer();
  let html: string;
  let importType: 'word' | 'pdf' | 'epub';

  if (isEpub) {
    importType = 'epub';
    html = await convertEpubToHtml(arrayBuffer);
  } else if (isPdf) {
    importType = 'pdf';
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    let fullText = '';

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const items = textContent.items as PdfTextItem[];

      const lines: PdfLine[] = [];
      const yPositions = new Set<number>();

      for (const item of items) {
        if (!item.str || !item.transform) continue;
        const y = Math.round(item.transform[5] * 10) / 10;
        yPositions.add(y);
      }

      const sortedYPositions = Array.from(yPositions).sort((a, b) => b - a);

      for (const y of sortedYPositions) {
        const lineItems = items
          .filter((item) => {
            if (!item.str || !item.transform) return false;
            const itemY = Math.round(item.transform[5] * 10) / 10;
            return Math.abs(itemY - y) < 0.1;
          })
          .map((item) => ({
            str: item.str,
            transform: item.transform,
            hasEOL: (item as any).hasEOL || false,
          }))
          .sort((a, b) => (a.transform[4] || 0) - (b.transform[4] || 0));

        if (lineItems.length > 0) {
          lines.push({ y, items: lineItems });
        }
      }

      const pageText = mergePdfLines(lines);

      if (pageText.trim().length > 0) {
        fullText += pageText.trim() + '\n\n';
      }
    }

    if (!fullText || fullText.trim().length === 0) {
      throw new Error('EMPTY_FILE');
    }

    const paragraphs = fullText
      .trim()
      .split(/\n+/)
      .map((para) => para.trim())
      .filter((para) => para.length > 0);

    html = paragraphs.map((para) => `<p>${para}</p>`).join('');
  } else {
    importType = 'word';
    if (isOdt) {
      html = await convertOdtToHtml(arrayBuffer);
    } else {
      const result = await mammoth.convertToHtml({ arrayBuffer });
      html = result.value;
    }

    if (!html || html.trim().length === 0) {
      throw new Error('EMPTY_FILE');
    }
  }

  return { html, importType, fileName: file.name };
}

export async function storeImportedFileAsResource(params: {
  file: File;
  projectId: string;
  documentId: string;
  token: string;
}): Promise<void> {
  await uploadImageResourceToS3({
    file: params.file,
    projectId: params.projectId,
    token: params.token,
    documentId: params.documentId,
    fromImport: true,
  });
}

interface UseFileImportOptions {
  onSuccess: (documentId: string, documentTitle: string, html: string, importType: 'word' | 'pdf' | 'epub') => Promise<void> | void;
  onError?: (error: Error) => void;
  createDocument: (title: string, content?: Uint8Array) => Promise<{ id: string; title: string; projectId?: string | null }>;
  token: string;
  t: (key: string) => string;
  showToast: (message: string) => void;
}

export function useFileImport({
  onSuccess,
  onError,
  createDocument,
  token,
  t,
  showToast,
}: UseFileImportOptions) {
  const [isImportingFile, setIsImportingFile] = useState(false);

  const importFile = async (file: File): Promise<ImportFileResult> => {
    if (!token || isImportingFile) {
      return FAILED_IMPORT_RESULT;
    }

    if (file.name.toLowerCase().endsWith('.pdf') && file.size > MAX_IMPORT_PDF_FILE_BYTES) {
      alert(t('import_pdf_error_too_large'));
      return FAILED_IMPORT_RESULT;
    }

    trackProjectImportStarted();
    setIsImportingFile(true);

    try {
      const { html, importType, fileName } = await processFile(file);
      const firstChapter = extractFirstChapter(html);
      const manuscriptText = htmlToPlainText(html);
      const suggestionsText = takeFirstWords(
        firstChapter?.text ?? manuscriptText,
        SUGGESTIONS_MAX_WORDS
      );

      const titlePattern = importType === 'pdf' ? /\.pdf$/i : importType === 'epub' ? /\.epub$/i : /\.(docx?|doc|odt)$/i;
      const defaultTitle = importType === 'pdf' || importType === 'epub' ? 'Imported Document' : 'Imported Project';
      const title = fileName.replace(titlePattern, '') || defaultTitle;

      const yjsContent = await convertHtmlToYjs(html);
      const document = await createDocument(title, yjsContent);

      try {
        const trackingResponse = await fetch(`${API_BASE}/api/word-imports`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            documentId: document.id,
            documentTitle: document.title,
            importType,
          }),
        });
        if (trackingResponse.ok) {
          await readJsonWithOnboarding(trackingResponse);
        }
      } catch (trackingError) {
        console.error('Error tracking file import:', trackingError);
      }

      if (document.projectId) {
        try {
          await storeImportedFileAsResource({
            file,
            projectId: document.projectId,
            documentId: document.id,
            token,
          });
        } catch (resourceError) {
          console.error('Error storing imported file as resource:', resourceError);
        }
      }

      await onSuccess(document.id, document.title, html, importType);
      trackProjectImportSuccess();
      showToast(t('import_file_success'));
      return {
        success: true,
        documentId: document.id,
        projectId: document.projectId ?? null,
        firstChapterWordCount: firstChapter?.wordCount ?? null,
        suggestionsText,
        manuscriptText,
      };
    } catch (err) {
      const error = err as Error;
      if (error.message === 'EMPTY_FILE') {
        trackProjectImportFailed('empty_file');
        alert(t('import_file_error_empty'));
      } else if (error.message === 'IMPORT_CONTENT_TOO_LARGE') {
        trackProjectImportFailed('content_too_large');
        alert(t('import_content_too_large'));
      } else if (error.message === 'PROJECT_LIMIT_REACHED') {
        trackProjectImportFailed('project_limit_reached');
      } else {
        trackProjectImportFailed('unknown');
        console.error('Error importing file:', err);
        alert(t('import_file_error'));
        if (onError) {
          onError(error);
        }
      }
      return FAILED_IMPORT_RESULT;
    } finally {
      setIsImportingFile(false);
    }
  };

  const handleImportFile = async (): Promise<void> => {
    if (!token || isImportingFile) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx,.doc,.odt,.pdf,.epub';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      await importFile(file);
    };
    input.click();
  };

  return {
    handleImportFile,
    importFile,
    isImportingFile,
  };
}
